import React, {useState} from "react"
import ReevaluateBusinessForm, {
	ReevaluatedBusinessApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateBusinessForm/ReevaluateBusinessForm"
import {ApplicationResubmitForms} from "./ReevaluatePage"
import {useModal} from "react-modal-hook"
import ReevaluateBusinessApplicationConfirmationModal from "../../components/ReevaluateApplication/ReevaluateConfirmationModal/ReevaluateBusinessApplicationConfirmationModal/ReevaluateBusinessApplicationConfirmationModal"
import {useNavigate} from "react-router-dom"
import {OkDocument} from "../../resources/common"
import {BusinessApplication} from "../../resources/application"
import ApplicationDetails from "./ApplicationDetails"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateBusinessApplicationProps {
	application: OkDocument<BusinessApplication>
	enableReevaluateApplication: boolean
}

function toBusinessApplicationFormData(
	businessApplication: BusinessApplication
): ReevaluatedBusinessApplicationData | undefined {
	if (
		!businessApplication.attributes.address ||
		!businessApplication.attributes.phone ||
		!businessApplication.attributes.ein ||
		!businessApplication.attributes.businessVertical ||
		!businessApplication.attributes.yearOfIncorporation
	) {
		return undefined
	}

	return {
		name: businessApplication.attributes.name,
		dba: businessApplication.attributes.dba ?? "",
		address: businessApplication.attributes.address,
		ein: businessApplication.attributes.ein,
		phone: businessApplication.attributes.phone,
		website: businessApplication.attributes.website,
		businessVertical: businessApplication.attributes.businessVertical,
		yearOfIncorporation: businessApplication.attributes.yearOfIncorporation,
		numberOfEmployees: businessApplication.attributes.numberOfEmployees,
		annualRevenue: businessApplication.attributes.annualRevenue,
		stockSymbol: businessApplication.attributes.stockSymbol,
		cashFlow: businessApplication.attributes.cashFlow,
		hasNoWebsite: businessApplication.attributes.website === undefined || businessApplication.attributes.website === "",
	}
}

function normalizeDbaValue(dba: string | undefined, application: BusinessApplication): string | undefined {
	const originalDba = application.attributes.dba
	return (dba === undefined || dba === "") && originalDba === undefined ? undefined : dba
}

export default function ReevaluateBusinessApplicationContainer({
	application,
	enableReevaluateApplication,
}: ReevaluateBusinessApplicationProps) {
	const navigate = useNavigate()

	const [businessReevaluatedData, setBusinessReevaluatedData] = useState<ReevaluatedBusinessApplicationData>(
		application.data.attributes as ReevaluatedBusinessApplicationData
	)

	const [showBusinessConfirmationModal, closeBusinessConfirmationModal] = useModal(
		() => (
			<ReevaluateBusinessApplicationConfirmationModal
				onSuccess={() => navigate(`/applications/${application.data.id}`)}
				close={closeBusinessConfirmationModal}
				application={application.data}
				enableReevaluateApplication={enableReevaluateApplication}
				reevaluatedData={businessReevaluatedData}
			/>
		),
		[businessReevaluatedData]
	)

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]
	const businessApplicationFormData = toBusinessApplicationFormData(application.data)

	const form = (
		<ApplicationDetails
			title={title}
			meta={meta}
			application={application}
			formId={ApplicationResubmitForms.businessForm}
		>
			{businessApplicationFormData && (
				<ReevaluateBusinessForm
					businessApplication={businessApplicationFormData}
					submitCallback={(values) => {
						setBusinessReevaluatedData({...values, dba: normalizeDbaValue(values.dba, application.data)})
						showBusinessConfirmationModal()
					}}
					formId={ApplicationResubmitForms.businessForm}
				/>
			)}
		</ApplicationDetails>
	)

	const error = (
		<Admonition
			type="is-danger"
			title="Could not be able to show resubmit form, please reach administator."
			message={""}
		/>
	)

	if (!businessApplicationFormData) {
		return error
	} else {
		return form
	}
}
